/**
 * OpenAI provider — the first production adapter for the AI platform.
 *
 * Transport note: the OpenAI key is a server secret, so this adapter never
 * calls api.openai.com from the browser. It posts platform-neutral requests to
 * the `ai-openai-proxy` edge function, which owns the key, quota enforcement,
 * and the vendor HTTP call. Everything OpenAI-specific (prompts, model ids,
 * response shape, error mapping, pricing) is contained in this file.
 *
 * Retries, timeouts, telemetry, metrics, cost and health reporting are all
 * provided by `AIService` / `ProviderManager` — this adapter only has to throw
 * the canonical error classes and return normalized usage.
 */

import { supabase } from '@/integrations/supabase/client';
import { configuredSupabasePublishableKey } from '@/integrations/supabase/env';
import { getSupabaseFunctionUrl } from '@/lib/supabaseFunctions';

import { getAISessionId } from '../session';

import type { AIProvider } from '../AIProvider';
import { registerModelPricing, estimateCost } from '../cost';
import {
  AIConfigurationError,
  AIError,
  AITimeoutError,
  ContentFilteredError,
  aiErrorFromStatus,
} from '../errors';
import {
  EVENT_DESCRIPTION_LIMITS,
  EVENT_TITLE_SUGGESTION_LIMITS,
  IMPROVE_LIMITS,
  MARKETING_LIMITS,
  eventDescriptionSystemPrompt,
  eventDescriptionUserPrompt,
  eventTitleSuggestionsSystemPrompt,
  eventTitleSuggestionsUserPrompt,
  improveSystemPrompt,
  improveUserPrompt,
  marketingSystemPrompt,
  marketingUserPrompt,
  marketingMaxOutputTokens,
  marketingWantsJson,
  PRICING_LIMITS,
  FORECAST_LIMITS,
  INSIGHTS_LIMITS,
  pricingSystemPrompt,
  pricingUserPrompt,
  forecastSystemPrompt,
  forecastUserPrompt,
  insightsSystemPrompt,
  insightsUserPrompt,
  ADMIN_INTELLIGENCE_LIMITS,
  adminIntelligenceSystemPrompt,
  adminIntelligenceUserPrompt,
  SEARCH_INTENT_LIMITS,
  SEARCH_TIME_FILTERS,
  capSearchQuery,
  searchIntentSystemPrompt,
  searchIntentUserPrompt,
  moderationSystemPrompt,
  translationSystemPrompt,
  type ImproveField,
  type ImprovePromptInput,
} from '../prompts';
import { normalizeUsage, estimateUsage, type NormalizedTokenUsage } from '../usage';
import type {
  AICapability,
  AIModel,
  AIProviderName,
  AIResponse,
  AIUsage,
  ChatRequest,
  ChatResponse,
  EventDescriptionRequest,
  EventDescriptionResponse,
  EventTitleRequest,
  EventTitleResponse,
  ForecastAdvice,
  ForecastRequest,
  ForecastResponse,
  InsightsAdvice,
  InsightsRequest,
  InsightsResponse,
  AdminIntelligenceRequest,
  AdminIntelligenceResponse,
  AdminIntelligenceSummary,
  SearchIntentRequest,
  SearchIntentResponse,
  SearchQueryIntent,
  MarketingContent,
  MarketingRequest,
  MarketingResponse,
  PricingAdvice,
  PricingRequest,
  PricingResponse,
  ModerationRequest,
  ModerationResponse,
  TextImprovementRequest,
  TextImprovementResponse,
  TranslationRequest,
  TranslationResponse,
} from '../types';

const PROVIDER: AIProviderName = 'openai';
const PROXY_FUNCTION = 'ai-openai-proxy';

/** Capabilities this adapter serves today. Embeddings/images come later. */
export const OPENAI_CAPABILITIES: readonly AICapability[] = [
  'chat',
  'text-improvement',
  'event-description',
  'event-title',
  'translation',
  'marketing',
  'pricing',
  'forecast',
  'insights',
  'admin-intelligence',
  'search-intent',
  'moderation',
];

/**
 * Public model metadata. Prices are per 1M tokens, USD (list prices, 2026-08).
 * The pricing registry is the single source of truth for cost estimation.
 */
export const OPENAI_MODELS: AIModel[] = [
  {
    id: 'gpt-4o-mini',
    provider: PROVIDER,
    label: 'GPT-4o mini',
    contextWindow: 128_000,
    capabilities: [...OPENAI_CAPABILITIES],
  },
  {
    id: 'gpt-4.1-mini',
    provider: PROVIDER,
    label: 'GPT-4.1 mini',
    contextWindow: 1_000_000,
    capabilities: [...OPENAI_CAPABILITIES],
  },
  {
    id: 'gpt-4.1',
    provider: PROVIDER,
    label: 'GPT-4.1',
    contextWindow: 1_000_000,
    capabilities: [...OPENAI_CAPABILITIES],
  },
];

registerModelPricing(
  {
    provider: PROVIDER,
    model: 'gpt-4o-mini',
    currency: 'USD',
    inputPerMillion: 0.15,
    outputPerMillion: 0.6,
    cachedInputPerMillion: 0.075,
    note: 'OpenAI list price, 2026-08',
  },
  {
    provider: PROVIDER,
    model: 'gpt-4.1-mini',
    currency: 'USD',
    inputPerMillion: 0.4,
    outputPerMillion: 1.6,
    cachedInputPerMillion: 0.1,
    note: 'OpenAI list price, 2026-08',
  },
  {
    provider: PROVIDER,
    model: 'gpt-4.1',
    currency: 'USD',
    inputPerMillion: 2,
    outputPerMillion: 8,
    cachedInputPerMillion: 0.5,
    note: 'OpenAI list price, 2026-08',
  },
);

/**
 * Attendee-facing capabilities a signed-out visitor may use. The proxy meters
 * these on a daily, identity-based quota (never an organizer entitlement) and
 * rate-limits them by IP.
 */
const ANONYMOUS_CAPABILITIES: ReadonlySet<AICapability> = new Set(['search-intent']);

interface ProxyRequest {
  capability: AICapability;
  /** Stable per-browser id, used only to meter signed-out visitors. */
  sessionId?: string;
  model?: string;
  system?: string;
  messages: { role: 'system' | 'user' | 'assistant'; content: string }[];
  maxOutputTokens?: number;
  temperature?: number;
  json?: boolean;
}

interface ProxyResponse {
  text: string;
  model: string;
  finishReason?: string;
  usage?: Record<string, number> | null;
}

interface ProxyErrorResponse {
  error?: string;
  code?: string;
  reason?: string;
  retryAfterMs?: number;
  requestId?: string;
  detail?: string;
  upstreamCode?: string | null;
}

const FINISH_REASONS: Record<string, AIResponse<unknown>['finishReason']> = {
  stop: 'stop',
  length: 'length',
  content_filter: 'content_filter',
};

export interface OpenAIProviderOptions {
  /** Override the edge function name (tests / staging experiments). */
  functionName?: string;
  /** Injected fetch, for tests. */
  fetchImpl?: typeof fetch;
  /** Resolve the caller's access token. Defaults to the Supabase session. */
  getAccessToken?: () => Promise<string | null>;
}

export class OpenAIProvider implements AIProvider {
  readonly name = PROVIDER;
  readonly capabilities = OPENAI_CAPABILITIES;

  private readonly functionName: string;
  private readonly fetchImpl: typeof fetch;
  private readonly getAccessToken: () => Promise<string | null>;

  constructor(options: OpenAIProviderOptions = {}) {
    this.functionName = options.functionName ?? PROXY_FUNCTION;
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.getAccessToken =
      options.getAccessToken ??
      (async () => (await supabase.auth.getSession()).data.session?.access_token ?? null);
  }

  /**
   * The API key lives on the server, so "configured" here means the transport
   * is reachable — i.e. a Supabase functions URL could be resolved.
   */
  isConfigured(): boolean {
    try {
      return !!getSupabaseFunctionUrl(this.functionName);
    } catch {
      return false;
    }
  }

  supports(capability: AICapability): boolean {
    return this.capabilities.includes(capability);
  }

  /** Model metadata for admin/debug surfaces. */
  listModels(): AIModel[] {
    return OPENAI_MODELS;
  }

  /* ------------------------------------------------------------------ */
  /* Transport                                                           */
  /* ------------------------------------------------------------------ */

  private async call(
    request: ProxyRequest,
    signal: AbortSignal | undefined,
    promptForEstimate: string,
  ): Promise<{ text: string; usage: NormalizedTokenUsage; model: string; finishReason?: string; durationMs: number }> {
    const token = await this.getAccessToken();
    const anonymousAllowed = ANONYMOUS_CAPABILITIES.has(request.capability);
    if (!token && !anonymousAllowed) {
      throw new AIConfigurationError('You must be signed in to use AI features.', {
        provider: PROVIDER,
        model: request.model,
      });
    }
    // Signed-out attendee traffic still goes through the proxy — the OpenAI key
    // never reaches the browser. The publishable key only opens the function
    // gateway; the proxy meters the visitor on their session identity.
    const bearer = token ?? configuredSupabasePublishableKey;
    const body = anonymousAllowed && !token
      ? { ...request, sessionId: request.sessionId ?? getAISessionId() }
      : request;

    const startedAt = Date.now();
    let response: Response;
    try {
      response = await this.fetchImpl(getSupabaseFunctionUrl(this.functionName), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${bearer}`,
          apikey: configuredSupabasePublishableKey,
        },
        body: JSON.stringify(body),
        signal,
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new AITimeoutError('AI request was cancelled', {
          provider: PROVIDER,
          model: request.model,
          cause: error,
        });
      }
      throw new AIError('unknown', 'Could not reach the AI service', {
        provider: PROVIDER,
        model: request.model,
        retryable: true,
        cause: error,
      });
    }

    const payload = (await response.json().catch(() => null)) as
      | (ProxyResponse & ProxyErrorResponse)
      | null;

    if (!response.ok) {
      const diagnosticSuffix = payload?.requestId
        ? ` (request ${payload.requestId})`
        : '';
      const message = `${payload?.error ?? 'AI request failed'}${diagnosticSuffix}`;

      // A structured error came from our proxy after it already completed its
      // auth/quota/upstream handling. Replaying it in the browser would create
      // a second quota claim for the same click. Only transport failures (no
      // structured proxy payload) remain eligible for platform retries.
      if (
        payload?.code === 'unknown' ||
        payload?.code === 'provider_unavailable' ||
        payload?.code === 'quota_unavailable'
      ) {
        const errorCode = payload.code === 'quota_unavailable'
          ? 'provider_unavailable'
          : payload.code;
        throw new AIError(errorCode, message, {
          provider: PROVIDER,
          model: request.model,
          reason: payload.code === 'quota_unavailable' ? 'quota_unavailable' : payload.reason,
          retryable: false,
          status: response.status,
        });
      }

      // The proxy has already normalized the failure. Preserve that canonical
      // code instead of treating every proxy 500 as a transient provider outage:
      // configuration/internal failures must not trigger two more quota claims.
      const canonicalStatus = payload?.code === 'configuration'
        ? 401
        : payload?.code === 'invalid_request'
          ? 400
          : payload?.code === 'quota_exceeded'
            ? 402
            : payload?.code === 'rate_limit'
              ? 429
              : payload?.code === 'timeout' || payload?.code === 'cancelled'
                ? 504
                : response.status;
      throw aiErrorFromStatus(canonicalStatus, message, {
        provider: PROVIDER,
        model: request.model,
        reason: payload?.reason,
        retryAfterMs: payload?.retryAfterMs,
      });
    }

    const text = payload?.text ?? '';
    if (payload?.finishReason === 'content_filter') {
      throw new ContentFilteredError('The AI blocked this content.', {
        provider: PROVIDER,
        model: payload?.model ?? request.model,
      });
    }

    const usage = payload?.usage
      ? normalizeUsage(payload.usage)
      : estimateUsage(promptForEstimate, text);

    return {
      text,
      usage,
      model: payload?.model ?? request.model ?? 'unknown',
      finishReason: payload?.finishReason,
      durationMs: Date.now() - startedAt,
    };
  }

  private envelope<T>(
    data: T,
    result: { usage: NormalizedTokenUsage; model: string; finishReason?: string; durationMs: number },
  ): AIResponse<T> {
    const usage: AIUsage = {
      provider: PROVIDER,
      model: result.model,
      tokens: result.usage,
      cost: estimateCost(PROVIDER, result.model, result.usage),
      durationMs: result.durationMs,
    };
    return {
      data,
      usage,
      finishReason: FINISH_REASONS[result.finishReason ?? 'stop'] ?? 'unknown',
    };
  }

  /* ------------------------------------------------------------------ */
  /* Capabilities                                                        */
  /* ------------------------------------------------------------------ */

  async chat(request: ChatRequest): Promise<ChatResponse> {
    const prompt = request.messages.map((m) => m.content).join('\n');
    const result = await this.call(
      {
        capability: 'chat',
        model: request.model,
        system: request.system,
        messages: request.messages,
        maxOutputTokens: request.maxOutputTokens,
      },
      request.signal,
      prompt,
    );
    return this.envelope({ text: result.text }, result);
  }

  async improveText(request: TextImprovementRequest): Promise<TextImprovementResponse> {
    const field: ImproveField = request.goal === 'title' ? 'title' : 'description';
    const promptInput: ImprovePromptInput = {
      field,
      language: request.language ?? 'en',
      tone: request.tone,
      text: request.text,
      context: request.metadata,
    };
    const limits = IMPROVE_LIMITS[field];

    const system = improveSystemPrompt(promptInput);
    const userPrompt = improveUserPrompt(promptInput);

    const result = await this.call(
      {
        capability: 'text-improvement',
        model: request.model,
        system,
        messages: [{ role: 'user', content: userPrompt }],
        maxOutputTokens: limits.maxOutputTokens,
        temperature: 0.7,
        json: true,
      },
      request.signal,
      `${system}\n${userPrompt}`,
    );

    const parsed = parseJsonObject(result.text);
    const improved = clampText(String(parsed?.improved ?? result.text).trim(), limits.maxChars);

    if (!improved) {
      throw new AIError('unknown', 'The AI did not return a suggestion.', {
        provider: PROVIDER,
        model: result.model,
        retryable: true,
      });
    }

    return this.envelope(
      { text: improved, rationale: parsed?.rationale ? String(parsed.rationale) : undefined },
      result,
    );
  }

  async generateEventDescription(
    request: EventDescriptionRequest,
  ): Promise<EventDescriptionResponse> {
    const promptInput = {
      title: request.title,
      category: request.category,
      location: request.location,
      startDate: request.startDate,
      highlights: request.highlights,
      language: request.language ?? 'en',
      tone: request.tone,
    };
    const details = eventDescriptionUserPrompt(promptInput);
    const system = eventDescriptionSystemPrompt(promptInput);
    const result = await this.call(
      {
        capability: 'event-description',
        model: request.model,
        system,
        messages: [{ role: 'user', content: details }],
        maxOutputTokens: EVENT_DESCRIPTION_LIMITS.maxOutputTokens,
        temperature: 0.7,
      },
      request.signal,
      `${system}\n${details}`,
    );
    return this.envelope(
      { text: clampText(result.text.trim(), EVENT_DESCRIPTION_LIMITS.maxChars) },
      result,
    );
  }

  /**
   * Phase 2E — multiple event title suggestions.
   *
   * Structured output (`json: true`) keeps parsing predictable: the model is
   * asked for `{"titles": [...]}`. Everything vendor-specific — prompt, JSON
   * mode, clamping, de-duplication — stays inside this adapter.
   */
  async generateEventTitles(request: EventTitleRequest): Promise<EventTitleResponse> {
    const count = Math.min(
      EVENT_TITLE_SUGGESTION_LIMITS.maxCount,
      Math.max(EVENT_TITLE_SUGGESTION_LIMITS.minCount, request.count ?? 5),
    );
    const promptInput = {
      language: request.language ?? 'en',
      category: request.category,
      location: request.location,
      description: request.description,
      audience: request.audience,
      keywords: request.keywords,
      tone: request.tone,
      count,
    };
    const system = eventTitleSuggestionsSystemPrompt(promptInput);
    const details = eventTitleSuggestionsUserPrompt(promptInput);

    const result = await this.call(
      {
        capability: 'event-title',
        model: request.model,
        system,
        messages: [{ role: 'user', content: details }],
        maxOutputTokens: EVENT_TITLE_SUGGESTION_LIMITS.maxOutputTokens,
        temperature: 0.8,
        json: true,
      },
      request.signal,
      `${system}\n${details}`,
    );

    const parsed = parseJsonObject(result.text);
    const raw = Array.isArray(parsed?.titles) ? parsed.titles : [];
    const seen = new Set<string>();
    const titles: string[] = [];
    for (const entry of raw) {
      const title = clampText(
        String(entry ?? '')
          .replace(/\s+/g, ' ')
          .replace(/^["'\s]+|["'\s.!]+$/g, '')
          .trim(),
        EVENT_TITLE_SUGGESTION_LIMITS.maxChars,
      );
      const key = title.toLowerCase();
      if (!title || seen.has(key)) continue;
      seen.add(key);
      titles.push(title);
      if (titles.length >= count) break;
    }

    if (!titles.length) {
      throw new AIError('unknown', 'The AI did not return any title suggestions.', {
        provider: PROVIDER,
        model: result.model,
        retryable: true,
      });
    }

    return this.envelope({ titles }, result);
  }

  async translate(request: TranslationRequest): Promise<TranslationResponse> {
    const system = translationSystemPrompt({
      targetLanguage: request.targetLanguage,
      sourceLanguage: request.sourceLanguage,
      doNotTranslate: request.doNotTranslate,
      format: request.format,
    });
    const result = await this.call(
      {
        capability: 'translation',
        model: request.model,
        system,
        messages: [{ role: 'user', content: request.text }],
        temperature: 0.2,
      },
      request.signal,
      `${system}\n${request.text}`,
    );
    return this.envelope({ text: result.text.trim() }, result);
  }

  /**
   * Phase 2G — AI Marketing Assistant.
   *
   * The three structured operations used Gemini tool-calling before the
   * migration; here they use JSON mode (`json: true`) and are parsed
   * defensively. The description operation stays free-form HTML, exactly as
   * before. Everything vendor-specific stays inside this adapter.
   */
  async generateMarketingContent(request: MarketingRequest): Promise<MarketingResponse> {
    const promptInput = {
      contentType: request.contentType,
      event: request.event,
      language: request.language ?? 'en',
      tone: request.tone ?? 'professional',
    };
    const system = marketingSystemPrompt(promptInput);
    const user = marketingUserPrompt(promptInput);

    const result = await this.call(
      {
        capability: 'marketing',
        model: request.model,
        system,
        messages: [{ role: 'user', content: user }],
        maxOutputTokens: marketingMaxOutputTokens(request.contentType),
        temperature: 0.7,
        json: marketingWantsJson(request.contentType),
      },
      request.signal,
      `${system}\n${user}`,
    );

    const emptyOutput = () =>
      new AIError('unknown', 'The AI returned an empty result. Please try again.', {
        provider: PROVIDER,
        model: result.model,
        reason: 'empty_output',
        retryable: true,
      });

    let data: MarketingContent;

    if (request.contentType === 'description') {
      const html = clampText(stripCodeFence(result.text).trim(), MARKETING_LIMITS.maxHtmlChars);
      if (!html) throw emptyOutput();
      data = { type: 'description', html };
    } else {
      const parsed = parseJsonObject(result.text);
      if (!parsed) throw emptyOutput();

      if (request.contentType === 'social_caption') {
        const short = clampText(text(parsed.short), MARKETING_LIMITS.maxCaptionChars);
        const medium = clampText(text(parsed.medium), MARKETING_LIMITS.maxCaptionChars);
        const long = clampText(text(parsed.long), MARKETING_LIMITS.maxCaptionChars);
        if (!short && !medium && !long) throw emptyOutput();
        data = { type: 'social_caption', short, medium, long };
      } else if (request.contentType === 'email_draft') {
        const subject = clampText(text(parsed.subject), MARKETING_LIMITS.maxSubjectChars);
        const previewText = clampText(
          text(parsed.preview_text ?? parsed.previewText),
          MARKETING_LIMITS.maxPreviewChars,
        );
        const bodyHtml = clampText(
          stripCodeFence(text(parsed.body_html ?? parsed.bodyHtml)),
          MARKETING_LIMITS.maxHtmlChars,
        );
        if (!subject && !bodyHtml) throw emptyOutput();
        data = { type: 'email_draft', subject, previewText, bodyHtml };
      } else {
        // Models occasionally answer with a bare array instead of the
        // documented `{ "hashtags": [...] }` envelope — accept both.
        const raw = Array.isArray(parsed)
          ? (parsed as unknown[])
          : Array.isArray(parsed.hashtags)
            ? parsed.hashtags
            : Array.isArray(parsed.tags)
              ? parsed.tags
              : [];
        const seen = new Set<string>();
        const hashtags: string[] = [];
        for (const entry of raw) {
          const tag = clampText(
            text(entry).replace(/^#+/, '').replace(/\s+/g, ''),
            MARKETING_LIMITS.maxHashtagChars,
          );
          const key = tag.toLowerCase();
          if (!tag || seen.has(key)) continue;
          seen.add(key);
          hashtags.push(tag);
          if (hashtags.length >= MARKETING_LIMITS.maxHashtags) break;
        }
        if (!hashtags.length) throw emptyOutput();
        data = { type: 'hashtags', hashtags };
      }
    }

    return this.envelope(data, result);
  }



  /* ------------------------------------------------------------------ */
  /* Phase 2H — Pricing assistant & event forecast                       */
  /* ------------------------------------------------------------------ */

  /**
   * Both operations narrate deterministic statistics computed server-side.
   * The adapter therefore only has to parse `{ summary, tips[] }` defensively:
   * the caller keeps the statistical result when this throws.
   */
  private parseAdvisory(
    raw: string,
    limits: { maxSummaryChars: number; maxTipChars: number; maxTips: number },
    model: string,
  ): { summary: string; tips: string[] } {
    const parsed = parseJsonObject(raw);
    // Some models answer with prose despite JSON mode — accept that as summary.
    const summary = clampText(
      parsed ? text(parsed.summary) : stripCodeFence(raw).trim(),
      limits.maxSummaryChars,
    );
    const rawTips = parsed && Array.isArray(parsed.tips) ? parsed.tips : [];
    const tips: string[] = [];
    for (const entry of rawTips) {
      const tip = clampText(text(entry), limits.maxTipChars);
      if (!tip) continue;
      tips.push(tip);
      if (tips.length >= limits.maxTips) break;
    }
    if (!summary && !tips.length) {
      throw new AIError('unknown', 'The AI returned an empty result. Please try again.', {
        provider: PROVIDER,
        model,
        reason: 'empty_output',
        retryable: true,
      });
    }
    return { summary, tips };
  }

  async generatePricingAdvice(request: PricingRequest): Promise<PricingResponse> {
    const promptInput = {
      stats: request.stats,
      currency: request.currency,
      language: request.language ?? 'en',
      location: request.location,
      capacity: request.capacity ?? null,
    };
    const system = pricingSystemPrompt(promptInput);
    const user = pricingUserPrompt(promptInput);

    const result = await this.call(
      {
        capability: 'pricing',
        model: request.model,
        system,
        messages: [{ role: 'user', content: user }],
        maxOutputTokens: PRICING_LIMITS.maxOutputTokens,
        temperature: 0.4,
        json: true,
      },
      request.signal,
      `${system}\n${user}`,
    );

    const data: PricingAdvice = this.parseAdvisory(result.text, PRICING_LIMITS, result.model);
    return this.envelope(data, result);
  }

  /**
   * Phase 2I — narrates the organizer's own deterministic analytics.
   * Defensive by design: the caller keeps the analytics when this throws.
   */
  async generateOrganizerInsights(request: InsightsRequest): Promise<InsightsResponse> {
    const promptInput = {
      stats: request.stats,
      currency: request.currency,
      language: request.language ?? 'en',
    };
    const system = insightsSystemPrompt(promptInput);
    const user = insightsUserPrompt(promptInput);

    const result = await this.call(
      {
        capability: 'insights',
        model: request.model,
        system,
        messages: [{ role: 'user', content: user }],
        maxOutputTokens: INSIGHTS_LIMITS.maxOutputTokens,
        temperature: 0.4,
        json: true,
      },
      request.signal,
      `${system}\n${user}`,
    );

    const parsed = parseJsonObject(result.text);
    // Some models answer with prose despite JSON mode — accept that as summary.
    const summary = clampText(
      parsed ? text(parsed.summary) : stripCodeFence(result.text).trim(),
      INSIGHTS_LIMITS.maxSummaryChars,
    );
    const topAction = clampText(
      parsed ? text(parsed.topAction ?? parsed.top_action) : '',
      INSIGHTS_LIMITS.maxActionChars,
    );
    if (!summary && !topAction) {
      throw new AIError('unknown', 'The AI returned an empty result. Please try again.', {
        provider: PROVIDER,
        model: result.model,
        reason: 'empty_output',
        retryable: true,
      });
    }
    const data: InsightsAdvice = { summary, topAction };
    return this.envelope(data, result);
  }

  /**
   * Phase 2J — narrates deterministic platform-wide admin statistics.
   * Defensive by design: the admin dashboard keeps its deterministic
   * analytics when this throws.
   */
  async generateAdminIntelligence(
    request: AdminIntelligenceRequest,
  ): Promise<AdminIntelligenceResponse> {
    const system = adminIntelligenceSystemPrompt();
    const user = adminIntelligenceUserPrompt({ stats: request.stats });

    const result = await this.call(
      {
        capability: 'admin-intelligence',
        model: request.model,
        system,
        messages: [{ role: 'user', content: user }],
        maxOutputTokens: ADMIN_INTELLIGENCE_LIMITS.maxOutputTokens,
        temperature: 0.3,
        json: true,
      },
      request.signal,
      `${system}\n${user}`,
    );

    const parsed = parseJsonObject(result.text);
    // A truncated or prose answer still yields a usable summary.
    const summary = clampText(
      parsed ? text(parsed.summary) : stripCodeFence(result.text).trim(),
      ADMIN_INTELLIGENCE_LIMITS.maxSummaryChars,
    );
    const biggestRisk = clampText(
      parsed ? text(parsed.biggest_risk ?? parsed.biggestRisk) : '',
      ADMIN_INTELLIGENCE_LIMITS.maxRiskChars,
    );
    const biggestOpportunity = clampText(
      parsed ? text(parsed.biggest_opportunity ?? parsed.biggestOpportunity) : '',
      ADMIN_INTELLIGENCE_LIMITS.maxOpportunityChars,
    );
    if (!summary && !biggestRisk && !biggestOpportunity) {
      throw new AIError('unknown', 'The AI returned an empty result. Please try again.', {
        provider: PROVIDER,
        model: result.model,
        reason: 'empty_output',
        retryable: true,
      });
    }
    const data: AdminIntelligenceSummary = { summary, biggestRisk, biggestOpportunity };
    return this.envelope(data, result);
  }

  async generateForecastAdvice(request: ForecastRequest): Promise<ForecastResponse> {
    const promptInput = {
      stats: request.stats,
      language: request.language ?? 'en',
      title: request.title,
      location: request.location,
      price: request.price,
      capacity: request.capacity ?? null,
    };
    const system = forecastSystemPrompt(promptInput);
    const user = forecastUserPrompt(promptInput);

    const result = await this.call(
      {
        capability: 'forecast',
        model: request.model,
        system,
        messages: [{ role: 'user', content: user }],
        maxOutputTokens: FORECAST_LIMITS.maxOutputTokens,
        temperature: 0.4,
        json: true,
      },
      request.signal,
      `${system}\n${user}`,
    );

    const data: ForecastAdvice = this.parseAdvisory(result.text, FORECAST_LIMITS, result.model);
    return this.envelope(data, result);
  }

  /**
   * Phase 2K — interprets the visitor's search wording. One request returns
   * structured intent, expanded terms and zero-result suggestions.
   *
   * Never a prerequisite for search results: callers keep the deterministic
   * result set when this throws.
   */
  async analyzeSearchIntent(request: SearchIntentRequest): Promise<SearchIntentResponse> {
    const query = capSearchQuery(request.query);
    if (!query) {
      throw new AIError('invalid_request', 'A search query is required.', {
        provider: PROVIDER,
        retryable: false,
      });
    }
    const promptInput = {
      query,
      categories: request.categories,
      location: request.location ?? null,
      language: request.language ?? 'en',
    };
    const system = searchIntentSystemPrompt(promptInput);
    const user = searchIntentUserPrompt(promptInput);

    const result = await this.call(
      {
        capability: 'search-intent',
        model: request.model,
        system,
        messages: [{ role: 'user', content: user }],
        maxOutputTokens: SEARCH_INTENT_LIMITS.maxOutputTokens,
        temperature: 0.2,
        json: true,
      },
      request.signal,
      `${system}\n${user}`,
    );

    const data = parseSearchIntent(result.text);
    return this.envelope(data, result);
  }

  async moderate(request: ModerationRequest): Promise<ModerationResponse> {
    if (!request.text) {
      throw new AIError('invalid_request', 'Image moderation is not supported yet.', {
        provider: PROVIDER,
        retryable: false,
      });
    }
    const system = moderationSystemPrompt({ categories: request.categories });

    const result = await this.call(
      {
        capability: 'moderation',
        model: request.model,
        system,
        messages: [{ role: 'user', content: request.text }],
        temperature: 0,
        json: true,
      },
      request.signal,
      `${system}\n${request.text}`,
    );
    const parsed = parseJsonObject(result.text) ?? {};
    const score = Number(parsed.score);
    return this.envelope(
      {
        score: Number.isFinite(score) ? Math.min(100, Math.max(0, score)) : 0,
        flagged: Boolean(parsed.flagged),
        categories: Array.isArray(parsed.categories) ? parsed.categories.map(String) : [],
        reason: parsed.reason ? String(parsed.reason) : undefined,
      },
      result,
    );
  }
}

/* -------------------------------------------------------------------- */
/* Helpers (OpenAI-specific, never exported outside this module)         */
/* -------------------------------------------------------------------- */

function parseJsonObject(raw: string): Record<string, unknown> | null {
  const trimmed = raw.trim().replace(/^```(?:json)?/i, '').replace(/```$/, '').trim();
  const attempt = (candidate: string): Record<string, unknown> | null => {
    try {
      const value = JSON.parse(candidate);
      return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  };
  return attempt(trimmed) ?? attempt(repairTruncatedJson(trimmed));
}

/**
 * Best-effort repair for a completion the model cut off mid-object (an output
 * budget that ran out). Closes an unterminated string and any open brackets so
 * the fields that DID arrive survive instead of the whole generation being
 * thrown away. Returns the input unchanged when nothing is repairable.
 */
function repairTruncatedJson(raw: string): string {
  if (!raw.startsWith('{') && !raw.startsWith('[')) return raw;
  const stack: string[] = [];
  let inString = false;
  let escaped = false;
  for (const char of raw) {
    if (inString) {
      if (escaped) escaped = false;
      else if (char === '\\') escaped = true;
      else if (char === '"') inString = false;
      continue;
    }
    if (char === '"') inString = true;
    else if (char === '{' || char === '[') stack.push(char === '{' ? '}' : ']');
    else if (char === '}' || char === ']') stack.pop();
  }
  if (!inString && stack.length === 0) return raw;
  let repaired = raw;
  if (escaped) repaired = repaired.slice(0, -1);
  if (inString) repaired += '"';
  // Drop a dangling `"key":` or trailing comma before closing.
  repaired = repaired.replace(/,\s*$/, '').replace(/,?\s*"[^"]*"\s*:\s*$/, '');
  return repaired + stack.reverse().join('');
}


/** Strip a ```-fenced block the model may have wrapped HTML/JSON in. */
function stripCodeFence(raw: string): string {
  return raw.trim().replace(/^```(?:html|json)?/i, '').replace(/```$/, '').trim();
}

/** Coerce an unknown JSON field into a trimmed string. */
function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim();
}



function clampText(text: string, max: number): string {
  return text.length <= max ? text : text.slice(0, max).trimEnd();
}

/* -------------------------------------------------------------------- */
/* Search intent parsing (Phase 2K)                                      */
/* -------------------------------------------------------------------- */

const stringList = (value: unknown, max: number): string[] => {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const entry of value) {
    const term = clampText(text(entry), SEARCH_INTENT_LIMITS.maxTermChars);
    const key = term.toLowerCase();
    if (!term || seen.has(key)) continue;
    seen.add(key);
    out.push(term);
    if (out.length >= max) break;
  }
  return out;
};

const priceRange = (value: unknown): { min: number; max: number } | null => {
  if (!value || typeof value !== 'object') return null;
  const raw = value as { min?: unknown; max?: unknown };
  const min = Number(raw.min);
  const max = Number(raw.max);
  if (!Number.isFinite(min) && !Number.isFinite(max)) return null;
  const lo = Number.isFinite(min) ? Math.max(0, min) : 0;
  const hi = Number.isFinite(max) ? Math.max(lo, max) : Number.MAX_SAFE_INTEGER;
  return { min: lo, max: hi };
};

/**
 * Tolerant parse of the single structured answer. Malformed or truncated JSON
 * degrades to an empty intent — never an exception — because the deterministic
 * search result must survive whatever the model returns.
 */
export function parseSearchIntent(raw: string): SearchQueryIntent {
  const parsed = parseJsonObject(raw) ?? {};
  const timeFilterRaw = text(parsed.time_filter ?? parsed.timeFilter).toLowerCase();
  const locationHint = clampText(
    text(parsed.location_hint ?? parsed.locationHint),
    SEARCH_INTENT_LIMITS.maxLocationChars,
  );
  return {
    categories: stringList(parsed.categories, SEARCH_INTENT_LIMITS.maxCategories),
    priceRange: priceRange(parsed.price_range ?? parsed.priceRange),
    timeFilter: (SEARCH_TIME_FILTERS as readonly string[]).includes(timeFilterRaw)
      ? timeFilterRaw
      : null,
    locationHint: locationHint || null,
    moodTags: stringList(parsed.mood_tags ?? parsed.moodTags, SEARCH_INTENT_LIMITS.maxMoodTags),
    expandedTerms: stringList(
      parsed.expanded_terms ?? parsed.expandedTerms,
      SEARCH_INTENT_LIMITS.maxExpandedTerms,
    ),
    suggestions: stringList(parsed.suggestions, SEARCH_INTENT_LIMITS.maxSuggestions),
  };
}


export const createOpenAIProvider = (options?: OpenAIProviderOptions) =>
  new OpenAIProvider(options);
